/**
 * Idioma del PORTAL PÚBLICO — quién decide y en qué orden.
 *
 * Vive aparte de `lib/settings.ts` a propósito: los ajustes del portal interno
 * son de la máquina de trabajo de la federación (URL del API, token, VAR) y no
 * tienen nada que ver con el navegador de un hincha que abre `/feva` una vez.
 * Compartir la clave habría hecho que cambiar el idioma en el portal público
 * le cambiara el idioma al estadístico que trabaja en esa misma máquina.
 *
 * Precedencia:
 *
 *  1. **Lo que el visitante eligió acá** (queda en este navegador).
 *  2. **El idioma del navegador**, si es uno de los tres que hablamos.
 *  3. **El `locale` de la organización**, que es su preferencia para los
 *     documentos que genera —el acta, los reportes—, NO una orden sobre en qué
 *     idioma tiene que leer un periodista brasileño. Por eso va tercero.
 *  4. Español.
 */
import { DEFAULT_LOCALE, isLocale, setLocale, type Locale } from '../../i18n/index';

const LOCALE_KEY = 'scout.public.locale';

/** Idioma guardado por el visitante, o `null`. Nunca lanza. */
export function savedPublicLocale(): Locale | null {
  try {
    const saved = globalThis.localStorage?.getItem(LOCALE_KEY) ?? null;
    return saved !== null && isLocale(saved) ? saved : null;
  } catch {
    // localStorage bloqueado (modo privado, iframe de OBS sin cookies): no es
    // un error, se sigue con el idioma del navegador.
    return null;
  }
}

/** Guarda la elección del visitante. Sin localStorage dura lo que la pestaña. */
export function rememberPublicLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(LOCALE_KEY, locale);
  } catch {
    // Ídem: se ignora a propósito.
  }
}

/** Idioma del navegador (`es-AR` → `es`), si es uno de los tres. */
export function browserLocale(): Locale | null {
  const tag = (globalThis.navigator?.language ?? '').slice(0, 2).toLowerCase();
  return isLocale(tag) ? tag : null;
}

/** Resuelve la precedencia de arriba y deja el idioma activo listo. */
export function applyInitialPublicLocale(orgLocale?: string): Locale {
  const chosen =
    savedPublicLocale() ??
    browserLocale() ??
    (orgLocale !== undefined && isLocale(orgLocale) ? orgLocale : DEFAULT_LOCALE);
  setLocale(chosen);
  return chosen;
}
